import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Palette, Trash2, AlertCircle, Eye, EyeOff, Info, Locate, Table, GripVertical, Download } from 'lucide-react';
import { cn } from '../../../lib/utils';
import Colorbar from './Colorbar';

const MapLayerItem = ({
  layer,
  isActive,
  onToggleVisibility,
  onRemove,
  onTogglePicker,
  onFilterLayer,
  onFlyToLayer,
  isDraggable
}) => {
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const paletteButtonRef = useRef(null);
  const infoButtonRef = useRef(null);

  // Update tooltip position when showing
  useEffect(() => {
    if (showInfoTooltip && infoButtonRef.current) {
      const rect = infoButtonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + 10,
        left: rect.right + 8
      });
    }
  }, [showInfoTooltip]);

  const isLoading = layer.status === 'loading';
  const hasError = layer.status === 'error';

  const handleDownload = () => {
    if (!layer.data || !layer.data.features || layer.data.features.length === 0) {
      return;
    }

    // Create a sanitized filename from the layer name
    const sanitizedName = layer.name
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase()
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || 'layer';

    // Convert GeoJSON to string
    const geoJsonString = JSON.stringify(layer.data, null, 2);

    // Create blob and download
    const blob = new Blob([geoJsonString], { type: 'application/geo+json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizedName}.geojson`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-2 transition-all relative">
      {/* Title row */}
      <div className="mb-1.5">
        <div className="flex items-center gap-2">
          {isDraggable && (
            <div className="cursor-grab active:cursor-grabbing p-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
              <GripVertical size={12} />
            </div>
          )}
          <h5 className="text-[11px] font-black text-gray-900 dark:text-white tracking-tight truncate" title={layer.name}>
            {layer.name}
          </h5>
          {hasError && (
            <AlertCircle size={12} className="text-red-500 shrink-0" />
          )}
          {!isLoading && (
            <button
              onClick={() => onToggleVisibility(layer.id)}
              disabled={hasError}
              className={cn(
                "ml-auto p-1 rounded-lg transition-all shrink-0",
                "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800",
                hasError && "opacity-50 cursor-not-allowed"
              )}
              title={layer.visible ? "Hide Layer" : "Show Layer"}
            >
              {layer.visible && !hasError ? (
                <Eye size={14} />
              ) : (
                <EyeOff size={14} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Collapsible content - smooth transition */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          layer.visible
            ? "max-h-[2000px] opacity-100 translate-y-0"
            : "max-h-0 opacity-0 -translate-y-2"
        )}
      >
        {/* Buttons row */}
        <div className="flex items-center justify-start gap-1 mb-1.5">
        {!isLoading && layer.dataset && (
          <>
            <button
              ref={infoButtonRef}
              onMouseEnter={() => setShowInfoTooltip(true)}
              onMouseLeave={() => setShowInfoTooltip(false)}
              className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
              title="Dataset Source"
            >
              <Info size={14} />
            </button>
            {/* Tooltip rendered via portal to avoid clipping */}
            {showInfoTooltip && typeof document !== 'undefined' && createPortal(
              <div
                className="fixed px-3 py-2.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-lg pointer-events-none z-[100] shadow-xl max-w-sm"
                style={{
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`
                }}
              >
                <div className="space-y-2">
                  {/* Dataset */}
                  <div>
                    <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Dataset</div>
                    <div className="text-[10px] font-black">{layer.dataset || layer.query?.dataset || 'N/A'}</div>
                  </div>

                  {/* Spatial Column */}
                  {layer.query?.spatial?.column && (
                    <div>
                      <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Spatial Column</div>
                      <div className="text-[10px] font-semibold">{layer.query.spatial.column}</div>
                    </div>
                  )}

                  {/* Value Column & Aggregation */}
                  {layer.query?.value && (
                    <div>
                      <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Value</div>
                      <div className="text-[10px] font-semibold">
                        {layer.query.value.aggregation?.toUpperCase() || 'SUM'} ({layer.query.value.column})
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  {layer.query?.filters && layer.query.filters.length > 0 && (
                    <div>
                      <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Filters</div>
                      <div className="space-y-1">
                        {layer.query.filters.map((filter, idx) => {
                          const values = Array.isArray(filter.values)
                            ? filter.values
                            : (filter.values ? [filter.values] : []);
                          const displayValues = values.length > 3
                            ? `${values.slice(0, 3).join(', ')}, ... (+${values.length - 3} more)`
                            : values.join(', ');
                          return (
                            <div key={idx} className="text-[10px] font-semibold">
                              {filter.column} {filter.op} {displayValues}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Time Range */}
                  {layer.query?.time && (
                    <div>
                      <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Time Range</div>
                      <div className="text-[10px] font-semibold">
                        {layer.query.time.column}: {layer.query.time.start === layer.query.time.end
                          ? layer.query.time.start.split('T')[0]
                          : `${layer.query.time.start.split('T')[0]} to ${layer.query.time.end.split('T')[0]}`
                        }
                      </div>
                    </div>
                  )}

                  {/* Dimensions */}
                  {layer.query?.dimensions && layer.query.dimensions.length > 0 && (
                    <div>
                      <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Dimensions</div>
                      <div className="text-[10px] font-semibold">
                        {layer.query.dimensions.map(d => d.alias || d.column).join(', ')}
                      </div>
                    </div>
                  )}

                  {/* Value Range Filter */}
                  {layer.valueFilter && (
                    <div>
                      <div className="text-[8px] font-bold text-gray-300 dark:text-gray-600 mb-0.5 uppercase tracking-widest">Value Range Filter</div>
                      <div className="text-[10px] font-semibold">
                        {layer.valueFilter.min.toFixed(2)} - {layer.valueFilter.max.toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900 dark:border-b-gray-100" />
              </div>,
              document.body
            )}
          </>
        )}
        {!isLoading && !hasError && layer.data && layer.data.features && layer.data.features.length > 0 && (
          <>
            <button
              onClick={() => setShowTable(!showTable)}
              className={cn(
                "p-1.5 rounded-lg transition-all",
                showTable
                  ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                  : "text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              )}
              title="Show Data Table"
            >
              <Table size={14} />
            </button>
            <button
              onClick={handleDownload}
              className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all rounded-lg"
              title="Download Layer (GeoJSON)"
            >
              <Download size={14} />
            </button>
            <button
              onClick={() => onFlyToLayer && onFlyToLayer(layer.id)}
              className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all rounded-lg"
              title="Fly to Layer"
            >
              <Locate size={14} />
            </button>
          </>
        )}
        {!isLoading && !hasError && (
          <button
            ref={paletteButtonRef}
            data-active-trigger={isActive}
            onClick={() => onTogglePicker(layer.id)}
            className={cn(
              "p-1.5 rounded-lg transition-all",
              isActive ? "bg-emerald-100 text-emerald-600 shadow-sm" : "text-gray-600 dark:text-gray-300 hover:text-emerald-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            )}
            title="Pick Color"
          >
            <Palette size={14} />
          </button>
        )}
        {!isLoading && (
          <button
            onClick={() => onRemove(layer.id)}
            className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all rounded-lg"
            title="Delete Layer"
          >
            <Trash2 size={14} />
          </button>
        )}
        </div>

        {/* Loading state */}
        {isLoading && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[8px] text-emerald-600 font-bold uppercase tracking-widest">
              {layer.progress || 'Loading...'}
            </p>
            {layer.progressPercent !== null && layer.progressPercent !== undefined && (
              <span className="text-[8px] text-emerald-600 font-black tabular-nums">
                {Math.round(layer.progressPercent)}%
              </span>
            )}
          </div>
          <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden relative">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
              style={{
                width: `${layer.progressPercent || 0}%`,
                boxShadow: '0 0 8px rgba(16, 185, 129, 0.4)'
              }}
            >
              <div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                style={{
                  animation: 'shimmer 2s infinite'
                }}
              />
            </div>
          </div>
        </div>
        )}

        {/* Error state */}
        {hasError && !isLoading && (
          <p className="text-[8px] text-red-500 font-bold uppercase tracking-widest">
            {layer.progress || 'Failed to load'}
          </p>
        )}

        {/* Data Table */}
        {showTable && !isLoading && !hasError && layer.data && layer.data.features && layer.data.features.length > 0 && (
        <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 z-10">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {(() => {
                    // Get all unique property keys from features
                    const allKeys = new Set();
                    layer.data.features.forEach(feature => {
                      if (feature.properties) {
                        Object.keys(feature.properties).forEach(key => allKeys.add(key));
                      }
                    });
                    const keys = Array.from(allKeys).filter(key => key !== 'geometry');
                    // Prioritize common keys
                    const priorityKeys = ['value', 'h3', 'lat', 'lon', 'geometry'];
                    const sortedKeys = [
                      ...priorityKeys.filter(k => keys.includes(k)),
                      ...keys.filter(k => !priorityKeys.includes(k))
                    ];
                    return sortedKeys.map(key => (
                      <th
                        key={key}
                        className="py-1.5 px-2 text-[8px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest bg-gray-50 dark:bg-gray-900"
                      >
                        {key}
                      </th>
                    ));
                  })()}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {layer.data.features.slice(0, 100).map((feature, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-900/50 transition-colors"
                  >
                    {(() => {
                      const allKeys = new Set();
                      layer.data.features.forEach(f => {
                        if (f.properties) {
                          Object.keys(f.properties).forEach(key => allKeys.add(key));
                        }
                      });
                      const keys = Array.from(allKeys).filter(key => key !== 'geometry');
                      const priorityKeys = ['value', 'h3', 'lat', 'lon', 'geometry'];
                      const sortedKeys = [
                        ...priorityKeys.filter(k => keys.includes(k)),
                        ...keys.filter(k => !priorityKeys.includes(k))
                      ];
                      return sortedKeys.map(key => {
                        const val = feature.properties?.[key];
                        let displayVal = val;
                        if (val === null || val === undefined) {
                          displayVal = '—';
                        } else if (typeof val === 'number') {
                          // Display numbers without abbreviations (use locale string for readability)
                          displayVal = val.toLocaleString(undefined, { maximumFractionDigits: 6 });
                        } else if (typeof val === 'object') {
                          displayVal = JSON.stringify(val);
                        } else {
                          displayVal = String(val);
                        }
                        return (
                          <td
                            key={key}
                            className="py-1.5 px-2 text-[9px] font-semibold text-gray-700 dark:text-gray-300 tabular-nums"
                          >
                            {displayVal}
                          </td>
                        );
                      });
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
            {layer.data.features.length > 100 && (
              <div className="px-2 py-1.5 text-[8px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                Showing first 100 of {layer.data.features.length} features
              </div>
            )}
          </div>
        </div>
        )}

        {/* Bottom: Legend (colorbar) */}
        {!isLoading && !hasError && layer.stats && layer.stats.min !== undefined && layer.stats.max !== undefined && (
          <div className="mt-1.5">
            <Colorbar layer={layer} onFilterLayer={onFilterLayer} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MapLayerItem;
